import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle, User, Phone, MapPin,
  CalendarCheck, CalendarClock, Hash, ArrowUpRight,
  CheckCircle2, XCircle, X, ShieldAlert, ShieldCheck, CreditCard, Receipt,
  StickyNote, ImageIcon, MessageSquare,
} from 'lucide-react';
import { depositsAPI } from '../../api/deposits';
import PageHeader  from '../../components/ui/PageHeader';
import StatusBadge from '../../components/ui/StatusBadge';

// ── Helpers ──────────────────────────────────────────────────
const formatINR = (val) =>
  `₹${Number(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const formatDateTime = (val) => {
  if (!val) return null;
  return new Date(val).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

// ── Info row ─────────────────────────────────────────────────
function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg mt-0.5"
           style={{ background: 'rgba(212,160,23,0.12)' }}>
        <Icon size={13} style={{ color: '#d4a017' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-wider mb-0.5"
           style={{ color: 'rgba(212,160,23,0.65)' }}>
          {label}
        </p>
        <p className="text-sm text-white break-words">{value}</p>
      </div>
    </div>
  );
}

// ── Section wrapper ──────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div className="card flex flex-col gap-4">
      <p className="text-[10px] font-semibold uppercase tracking-widest"
         style={{ color: 'rgba(212,160,23,0.6)' }}>
        {title}
      </p>
      {children}
    </div>
  );
}

// ── Skeleton ─────────────────────────────────────────────────
function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="skeleton h-52 rounded-2xl" />
      <div className="skeleton h-36 rounded-2xl" />
      <div className="skeleton h-28 rounded-2xl" />
    </div>
  );
}

// ── Confirm Action Sheet ─────────────────────────────────────
function ConfirmSheet({ action, deposit, onClose, onConfirm, submitting }) {
  const [remarks, setRemarks] = useState('');

  const isApprove = action === 'approved';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={!submitting ? onClose : undefined}
    >
      <div
        className="w-full max-w-app animate-fade-up rounded-t-3xl p-6 pb-8"
        style={{
          background: 'linear-gradient(180deg,#0e2060 0%,#060f38 100%)',
          border: '1px solid rgba(212,160,23,0.2)',
          borderBottom: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl flex-shrink-0"
                 style={{
                   background: isApprove
                     ? 'rgba(5,150,105,0.15)'
                     : 'rgba(239,68,68,0.15)',
                   border: `1px solid ${isApprove
                     ? 'rgba(5,150,105,0.3)'
                     : 'rgba(239,68,68,0.3)'}`,
                 }}>
              {isApprove
                ? <CheckCircle2 size={18} style={{ color: '#6ee7b7' }} />
                : <ShieldAlert   size={18} style={{ color: '#fca5a5' }} />}
            </div>
            <div>
              <h3 className="font-serif text-base font-semibold text-white">
                {isApprove ? 'Approve Deposit' : 'Reject Deposit'}
              </h3>
              <p className="text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {formatINR(deposit.amount)} · {deposit.user?.name ?? 'Deleted User'}
              </p>
            </div>
          </div>
          {!submitting && (
            <button onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.08)' }}>
              <X size={14} className="text-white" />
            </button>
          )}
        </div>

        {/* Remarks */}
        <div className="mb-5 flex flex-col gap-1.5">
          <label className="input-label">
            Remarks <span style={{ color: 'rgba(255,255,255,0.3)' }}>(optional)</span>
          </label>
          <textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder={isApprove
              ? 'e.g. Amount received and verified'
              : 'e.g. Amount not received in account'}
            rows={2}
            maxLength={500}
            disabled={submitting}
            className="input-field resize-none disabled:opacity-50"
          />
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="flex-1 rounded-2xl py-3.5 text-sm font-semibold
                       transition-all duration-200 active:scale-95 disabled:opacity-50"
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            Cancel
          </button>

          <button
            onClick={() => onConfirm(remarks.trim() || undefined)}
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl
                       py-3.5 text-sm font-semibold transition-all duration-200
                       active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{
              background: isApprove
                ? 'linear-gradient(135deg,#059669 0%,#047857 100%)'
                : 'linear-gradient(135deg,#dc2626 0%,#b91c1c 100%)',
              boxShadow: isApprove
                ? '0 4px 16px rgba(5,150,105,0.35)'
                : '0 4px 16px rgba(220,38,38,0.35)',
              color: '#fff',
            }}
          >
            {submitting ? (
              <span className="inline-block h-4 w-4 rounded-full border-2
                               border-white/30 border-t-white animate-spin" />
            ) : (
              <>
                {isApprove
                  ? <CheckCircle2 size={15} />
                  : <XCircle      size={15} />}
                {isApprove ? 'Approve' : 'Reject'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────
export default function DepositDetailPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [deposit,    setDeposit]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // action sheet state
  const [actionType,  setActionType]  = useState(null); // 'approved' | 'rejected' | null
  const [submitting,  setSubmitting]  = useState(false);
  const [actionError, setActionError] = useState('');
  const [successMsg,  setSuccessMsg]  = useState('');

  // GET /api/admin/deposits/{id}
  const fetchDeposit = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await depositsAPI.getById(id);
      if (data.status) setDeposit(data.data);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? 'Deposit not found.'
          : err?.response?.data?.message || 'Failed to load deposit details.'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDeposit(); }, [fetchDeposit]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(''), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);

  // PUT /api/admin/deposits/{id}/status
  const handleConfirm = async (remarks) => {
    setSubmitting(true);
    setActionError('');
    try {
      const { data } = await depositsAPI.updateStatus(id, {
        status: actionType,
        ...(remarks ? { remarks } : {}),
      });

      if (data.status) {
        // Update local deposit state in-place — no full refetch needed
        setDeposit((prev) => ({
          ...prev,
          status:      data.data.status,
          approved_at: data.data.approved_at,
        }));
        setSuccessMsg(data.message);
        setActionType(null);
      } else {
        setActionError(data.message || 'Update failed.');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        'Something went wrong. Please try again.';
      // 422 = already processed — close sheet and show inline error
      if (err?.response?.status === 422) {
        setActionType(null);
        setActionError(msg);
      } else {
        setActionError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────
  if (loading) {
    return (
      <div>
        <PageHeader title="Deposit Detail" showBack />
        <DetailSkeleton />
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────
  if (error) {
    return (
      <div>
        <PageHeader title="Deposit Detail" showBack />
        <div className="flex flex-col items-center justify-center gap-3 py-20 px-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-full"
               style={{ background: 'rgba(239,68,68,0.12)' }}>
            <AlertCircle size={24} style={{ color: '#fca5a5' }} />
          </div>
          <p className="text-sm text-center" style={{ color: '#fca5a5' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!deposit) return null;

  const isPending  = deposit.status === 'pending';
  const isApproved = deposit.status === 'approved';
  const isRejected = deposit.status === 'rejected';

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-0">
      <PageHeader title={`Deposit #${deposit.id}`} showBack />

      <div className="flex flex-col gap-4 p-4">

        {/* ── Success banner ── */}
        {successMsg && (
          <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
               style={{
                 background: 'rgba(5,150,105,0.15)',
                 border: '1px solid rgba(5,150,105,0.35)',
                 color: '#6ee7b7',
               }}>
            <CheckCircle2 size={15} className="flex-shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* ── Action error banner ── */}
        {actionError && (
          <div className="flex items-center gap-2 rounded-xl px-4 py-3 text-sm animate-fade-in"
               style={{
                 background: 'rgba(239,68,68,0.12)',
                 border: '1px solid rgba(239,68,68,0.3)',
                 color: '#fca5a5',
               }}>
            <AlertCircle size={15} className="flex-shrink-0" />
            <span>{actionError}</span>
          </div>
        )}

        {/* ── Amount hero card ── */}
        <div className="rounded-2xl p-5 flex flex-col gap-4"
             style={{
               background: isApproved
                 ? 'linear-gradient(135deg,#b8860b 0%,#d4a017 40%,#f0c040 100%)'
                 : isRejected
                 ? 'linear-gradient(135deg,#7f1d1d 0%,#991b1b 100%)'
                 : 'linear-gradient(135deg,#1448c4 0%,#0a2260 100%)',
               boxShadow: isApproved
                 ? '0 8px 32px rgba(212,160,23,0.35)'
                 : isRejected
                 ? '0 8px 32px rgba(220,38,38,0.25)'
                 : '0 8px 32px rgba(20,72,196,0.35)',
             }}>

          {/* Top row: Deposit ID + status + payment badge */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Hash size={13} style={{ color: 'rgba(255,255,255,0.6)' }} />
              <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Deposit ID {deposit.id}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {deposit.razorpay_payment_id && (
                <span
                  className="inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 text-[9px] font-semibold"
                  style={{ background: 'rgba(5,150,105,0.25)', color: '#6ee7b7', border: '1px solid rgba(5,150,105,0.4)' }}
                >
                  <ShieldCheck size={9} />
                  Razorpay
                </span>
              )}
              <StatusBadge status={deposit.status} />
            </div>
          </div>

          {/* Amount */}
          <div>
            <p className="text-xs font-medium uppercase tracking-widest mb-1"
               style={{ color: 'rgba(255,255,255,0.65)' }}>
              Amount
            </p>
            <p className="text-4xl font-bold font-serif text-white">
              {formatINR(deposit.amount)}
            </p>
          </div>

          {/* Date strip */}
          <div className="grid grid-cols-2 gap-3 pt-3 border-t"
               style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
            <div>
              <p className="text-[10px] uppercase tracking-wider mb-0.5"
                 style={{ color: 'rgba(255,255,255,0.55)' }}>
                Deposited
              </p>
              <p className="text-xs font-medium text-white">
                {formatDateTime(deposit.deposited_at)}
              </p>
            </div>
            {deposit.approved_at && (
              <div>
                <p className="text-[10px] uppercase tracking-wider mb-0.5"
                   style={{ color: 'rgba(255,255,255,0.55)' }}>
                  {isRejected ? 'Processed' : 'Approved'}
                </p>
                <p className="text-xs font-medium text-white">
                  {formatDateTime(deposit.approved_at)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Approve / Reject actions — only for pending ── */}
        {isPending && (
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => { setActionError(''); setActionType('approved'); }}
              className="flex items-center justify-center gap-2 rounded-2xl py-3.5
                         text-sm font-semibold transition-all duration-200 active:scale-95"
              style={{
                background: 'linear-gradient(135deg,#059669 0%,#047857 100%)',
                boxShadow: '0 4px 16px rgba(5,150,105,0.3)',
                color: '#fff',
              }}
            >
              <CheckCircle2 size={16} />
              Approve
            </button>

            <button
              onClick={() => { setActionError(''); setActionType('rejected'); }}
              className="flex items-center justify-center gap-2 rounded-2xl py-3.5
                         text-sm font-semibold transition-all duration-200 active:scale-95"
              style={{
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#fca5a5',
              }}
            >
              <XCircle size={16} />
              Reject
            </button>
          </div>
        )}

        {/* ── User info section ── */}
        <Section title="Account Holder">
          <InfoRow icon={User}  label="Full Name" value={deposit.user?.name ?? 'Deleted User'} />
          <InfoRow icon={Phone} label="Mobile"    value={deposit.user?.mobile} />
          {deposit.user?.address && (
            <InfoRow icon={MapPin} label="Address" value={deposit.user.address} />
          )}
          {deposit.user && (
            <button
              onClick={() => navigate(`/users/${deposit.user.id}`)}
              className="mt-1 flex items-center gap-2 self-start rounded-xl px-4 py-2
                         text-xs font-semibold transition-all active:scale-95"
              style={{
                background: 'rgba(20,72,196,0.2)',
                border: '1px solid rgba(20,72,196,0.35)',
                color: '#93c5fd',
              }}
            >
              <User size={12} />
              View Full Profile
              <ArrowUpRight size={12} />
            </button>
          )}
        </Section>

        {/* ── Timeline section ── */}
        <Section title="Timeline">
          <InfoRow
            icon={CalendarClock}
            label="Deposit Submitted"
            value={formatDateTime(deposit.deposited_at)}
          />
          {deposit.approved_at && (
            <InfoRow
              icon={CalendarCheck}
              label={isRejected ? 'Processed At' : 'Approved At'}
              value={formatDateTime(deposit.approved_at)}
            />
          )}
        </Section>

        {/* ── Payment details section ── */}
        {deposit.razorpay_payment_id ? (
          <Section title="Payment Details">
            {/* Paid badge */}
            <div className="flex items-center gap-2.5 rounded-xl px-3 py-2.5"
                 style={{ background: 'rgba(5,150,105,0.12)', border: '1px solid rgba(5,150,105,0.25)' }}>
              <ShieldCheck size={16} style={{ color: '#6ee7b7', flexShrink: 0 }} />
              <div>
                <p className="text-xs font-semibold" style={{ color: '#6ee7b7' }}>
                  Payment Verified
                </p>
                <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  Secured via Razorpay
                </p>
              </div>
            </div>

            <InfoRow
              icon={CreditCard}
              label="Payment ID"
              value={deposit.razorpay_payment_id}
            />

            {deposit.razorpay_order_id && (
              <InfoRow
                icon={Receipt}
                label="Order ID"
                value={deposit.razorpay_order_id}
              />
            )}
          </Section>
        ) : (
          <Section title="Payment Details">
            {/* Manual badge */}
            <div className="flex items-center gap-2.5 rounded-xl px-3 py-2.5"
                 style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <Hash size={16} style={{ color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
                Manual bank / UPI transfer
              </p>
            </div>

            {/* Reference number */}
            {deposit.reference_number && (
              <InfoRow
                icon={Hash}
                label="Reference / UTR Number"
                value={deposit.reference_number}
              />
            )}

            {/* User note */}
            {deposit.user_note && (
              <InfoRow
                icon={StickyNote}
                label="Note from User"
                value={deposit.user_note}
              />
            )}

            {/* Screenshot */}
            {deposit.screenshot_url && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5">
                  <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg"
                       style={{ background: 'rgba(212,160,23,0.12)' }}>
                    <ImageIcon size={13} style={{ color: '#d4a017' }} />
                  </div>
                  <p className="text-[10px] font-medium uppercase tracking-wider"
                     style={{ color: 'rgba(212,160,23,0.65)' }}>
                    Payment Screenshot
                  </p>
                </div>
                <a href={deposit.screenshot_url} target="_blank" rel="noreferrer">
                  <img
                    src={deposit.screenshot_url}
                    alt="Payment screenshot"
                    className="w-full rounded-xl object-contain"
                    style={{
                      maxHeight: '280px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  />
                  <p className="text-[10px] mt-1 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>
                    Tap to open full image
                  </p>
                </a>
              </div>
            )}
          </Section>
        )}

        {/* ── Admin remarks (shown after approval / rejection) ── */}
        {deposit.admin_remarks && (
          <Section title="Admin Remarks">
            <InfoRow
              icon={MessageSquare}
              label="Remarks"
              value={deposit.admin_remarks}
            />
          </Section>
        )}

      </div>

      {/* ── Confirmation sheet ── */}
      {actionType && (
        <ConfirmSheet
          action={actionType}
          deposit={deposit}
          onClose={() => !submitting && setActionType(null)}
          onConfirm={handleConfirm}
          submitting={submitting}
        />
      )}
    </div>
  );
}
